import barcode_regex from "@pdfking/constants/barcode"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import {
  contains,
  filter,
  length,
  object_map,
  ordered_object,
  scan,
} from "@pdfking/rfunc"
import { Items, RecordMerge, SortBy, Values } from "@pdfking/tfunc"
import { Icon } from "@pdfking/workflow/components/Icon"
import fire_event from "@pdfking/workflow/utils/fire_event"
import make_translations from "@pdfking/workflow/utils/make_translations"
import proxy from "@pdfking/workflow/utils/proxy"
import { url_query_encode } from "@pdfking/workflow/utils/urlutilities"
import autocomplete from "../../constants/autocomplete"
import { CloseMixin } from "../../mixins/Close"
import { FreezeMixin } from "../../mixins/Freeze"
import { SearchMixin } from "../../mixins/Search"
import change_window_location from "../../navigation/change_window_location"
import state from "../../state/main"
import user from "../../state/user"
import cleanup from "../../utils/barcode_cleanup"
import get_django_url from "../../utils/get_django_url"
import is_firefox from "../../utils/is_firefox"
import navigate from "../navigate"
import { SEARCH_VIEW } from "../urls"
import { Filters } from "./layout/Filters"
import { Results } from "./layout/Results"

export const Search = CreateRenderFunction({
  computed: {
    ...proxy(autocomplete(), {
      loading: "loading",
      pages_index: "all_options",
      tags_index: "tags",
    } as const),
    barcode_string() {
      return cleanup(this.query)
    },
    can_use_barcode() {
      return user().is_staff
    },
    default_results(): any[] {
      return user().is_staff ? Values(this.all_options || {}) : []
    },

    filtered_results() {
      return object_map(
        (o: any) => o.id,
        (o: any) => o,
        SortBy(
          filter((r: any) => r.in_search, Values(this.results)),
          (r: any) => [r.kind.sequence, r.score]
        )
      )
    },
    filters() {
      const filters = []

      if (length(this.grouped_results) > 1) {
        filters.push({
          badge: length(this.filtered_results),
          icon: "DASHBOARD",
          id: null,
          title: this.translations.actions.ALL,
        })
      }

      for (const [group, inner] of Items(this.grouped_results)) {
        filters.push({
          badge: length(inner),
          icon: this.tags[group].icon,
          id: group,
          title: this.tags[group].name,
        })
      }

      return filters
    },
    grouped_results() {
      const results = ordered_object()

      scan(
        (r: any) =>
          scan((t: any) => {
            if (!results[t.id]) {
              results[t.id] = [r]
            } else {
              // @ts-expect-error TS2339
              results[t.id].push(r)
            }
          }, r.tags),
        this.filtered_results
      )

      return results
    },
    is_barcode() {
      if (this.can_use_barcode && this.query) {
        const bits = filter(
          (s: string) => barcode_regex.barcode.test(s),
          this.query.split(barcode_regex.divider)
        )
        return length(bits) > 0
      }
      return false
    },
    results_data() {
      return (
        this.filter
          ? this.grouped_results[this.filter] || []
          : Values(this.filtered_results)
      ) as any[]
    },
    translations: () => make_translations("actions"),
  },
  props: {
    filter: "" as string,
    query: "" as string,
    send: null as number | null,
  },
  data: {
    expand_size: 10,
    expanded: {} as Record<string, boolean>,
    tag: null,
    toggled_class: "search-body",
  },

  methods: {
    contains,
    is_expanded(id: string) {
      return this.expanded[id]
    },
    is_firefox,
    length,
    searchtab(selected: string) {
      const params: Record<string, any> = {}
      if (this.query) params.query = this.query
      if (selected) {
        params.filter = selected
      }
      navigate(SEARCH_VIEW, params)

      state().track_event("search_tab", "search")
    },
    submit_barcode() {
      if (this.is_barcode) {
        const qs = url_query_encode({ barcode: this.barcode_string })

        get_django_url("admin_barcode").then((url: string) =>
          change_window_location(`${url}?${qs}`)
        )

        this.close()
      }
    },
    toggle(info: any) {
      state().track_event("search_link", "search")
      change_window_location(info.url)
      this.close()
    },
    toggle_expanded(id: string) {
      state().track_event("search_expand", "search")

      this.expanded = RecordMerge(this.expanded, { [id]: !this.expanded[id] })
    },
  },

  mixins: [CloseMixin, SearchMixin, FreezeMixin],

  mounted() {
    fire_event(document, "focus_search")
  },

  render() {
    return (
      <div class="position-absolute bg-white z-2 w-100 main-menu overflow-hidden">
        <div class="container position-relative d-flex flex-column h-100">
          <button
            class="btn-close position-absolute top-0 end-0 mx-3 my-4 p-2 rounded-circle border bg-white d-none d-lg-block"
            type="button"
            onClick={this.close}
          />
          {this.is_barcode ? (
            <a
              class="display-4 mt-4 text-decoration-none"
              href="#"
              onClick={this.submit_barcode}
            >
              <Icon name="BARCODE" />
              {this.barcode_string}
            </a>
          ) : this.loading.public_pages || this.is_loading ? (
            <div class="container text-center">
              <div class="spinner-border muted m-5 p-5"></div>
            </div>
          ) : (
            <div class="row h-100">
              <div class="col order-lg-last overflow-scroll overscroll-contain no-scrollbar h-100">
                <Results pages={this.results_data} />
              </div>
              <div class="col-12 col-lg-3 overflow-scroll overscroll-contain no-scrollbar h-100 border-end g-0 me-2">
                <Filters
                  action={this.searchtab}
                  class="mt-4"
                  filters={this.filters}
                  selected={this.filter}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    )
  },

  watch: {
    send(_v: any) {
      this.submit_barcode()
    },
  },
})
